import type { StripeElementsOptions } from '@stripe/stripe-js';
import type {
    IFundWalletPayload,
    IFundWalletPaystackResponse,
    IFundWalletResponse,
    IFundWalletStripeResponse,
    IWalletFundOptions,
    IWalletFundPreset,
    WalletFundProvider,
} from '../../redux/buzzbridge/types';
import { ROUTES } from '../../types/global/routes.types';
import { isAdminTestPreset, isCustomGrossPaymentMode } from './walletFundOptionsUtils';
import type { ICampaignWalletSalesCalc } from './walletSalesCalcUtils';
import { grossChargeCentsFromSalesCalc, resolveWalletCreditUsdCentsFromSalesCalc } from './walletSalesCalcUtils';

export const WALLET_FUND_PAYSTACK_REF_KEY = 'campaign_wallet_fund_paystack_ref';
export const WALLET_FUND_CHECKOUT_PROVIDER_KEY = 'campaign_wallet_fund_checkout_provider';
/** After wallet top-up, return here (e.g. campaign detail ?fund=1). */
export const WALLET_FUND_RETURN_URL_KEY = 'campaign_wallet_fund_return_url';

export function storeWalletFundReturnUrl(url: string): void {
    try {
        sessionStorage.setItem(WALLET_FUND_RETURN_URL_KEY, url);
    } catch {
        /* ignore */
    }
}

export function readWalletFundReturnUrl(): string | null {
    try {
        return sessionStorage.getItem(WALLET_FUND_RETURN_URL_KEY);
    } catch {
        return null;
    }
}

export function clearWalletFundReturnUrl(): void {
    try {
        sessionStorage.removeItem(WALLET_FUND_RETURN_URL_KEY);
    } catch {
        /* ignore */
    }
}

export function getWalletFundCallbackUrl(): string {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}${ROUTES.BRAND_WALLET_FUND_CALLBACK}`;
}

export function isPaystackFundResponse(res: IFundWalletResponse): res is IFundWalletPaystackResponse {
    return (
        (res.provider === 'paystack' || res.provider === 'flutterwave') &&
        Boolean((res as IFundWalletPaystackResponse).authorization_url)
    );
}

export function isHostedFundResponse(res: IFundWalletResponse): res is IFundWalletPaystackResponse {
    return isPaystackFundResponse(res);
}

export function isStripeFundResponse(res: IFundWalletResponse): res is IFundWalletStripeResponse {
    return Boolean((res as IFundWalletStripeResponse).clientSecret);
}

export function paystackCheckoutAvailable(options: IWalletFundOptions | null): boolean {
    const rail = options?.payment_rail;
    return Boolean(rail?.use_paystack && rail?.paystack_available !== false);
}

export function flutterwaveCheckoutAvailable(options: IWalletFundOptions | null): boolean {
    const rail = options?.payment_rail;
    return Boolean(rail?.use_flutterwave && rail?.flutterwave_available !== false);
}

export function stripeCheckoutAvailable(options: IWalletFundOptions | null): boolean {
    const rail = options?.payment_rail;
    if (!rail) return true;
    return rail.use_stripe !== false && rail.stripe_available !== false;
}

export function defaultWalletFundProvider(options: IWalletFundOptions | null): WalletFundProvider {
    const rail = options?.payment_rail;
    if (paystackCheckoutAvailable(options) && rail?.preferred_provider === 'paystack') return 'paystack';
    if (
        paystackCheckoutAvailable(options) &&
        rail?.preferred_provider !== 'stripe' &&
        rail?.preferred_provider !== 'flutterwave'
    ) {
        return 'paystack';
    }
    if (flutterwaveCheckoutAvailable(options) && rail?.preferred_provider === 'flutterwave') return 'flutterwave';
    if (
        flutterwaveCheckoutAvailable(options) &&
        !paystackCheckoutAvailable(options) &&
        rail?.preferred_provider !== 'stripe'
    ) {
        return 'flutterwave';
    }
    return 'stripe';
}

export function formatPaymentRailChannels(options: IWalletFundOptions | null): string | null {
    const channels = options?.payment_rail?.channels;
    if (!channels?.length) return null;
    return channels.map(c => c.replace(/_/g, ' ')).join(' · ');
}

/**
 * Stripe Elements options for wallet fund checkout.
 * Must mirror POST /campaign-wallet/fund PaymentIntent setup_future_usage:
 * - save_card true  → off_session
 * - save_card false → omit (PI has null; do NOT use 'on_session')
 */
export function buildWalletStripeElementsOptions(params: {
    amount: number;
    currency: string;
    saveCard: boolean;
    appearance: StripeElementsOptions['appearance'];
}): StripeElementsOptions {
    return {
        mode: 'payment',
        amount: params.amount,
        currency: params.currency,
        appearance: params.appearance,
        ...(params.saveCard ? { setup_future_usage: 'off_session' as const } : {}),
    };
}

export function buildCampaignWalletFundBody(
    walletCreditUsdCents: number,
    opts?: {
        provider?: WalletFundProvider;
        callbackUrl?: string;
        saveCard?: boolean;
        preset?: IWalletFundPreset | null;
        fundOptions?: IWalletFundOptions | null;
        /** Sales-calc preview for custom gross Paystack checkout */
        feePreview?: ICampaignWalletSalesCalc | null;
        customPaymentChargeCents?: number | null;
    },
): IFundWalletPayload {
    const preset = opts?.preset;
    const walletCredit = preset && isAdminTestPreset(preset) ? preset.wallet_credit.amount_cents : walletCreditUsdCents;

    const body: IFundWalletPayload = {};

    if (opts?.provider) body.provider = opts.provider;
    const needsCallback = opts?.provider === 'paystack' || opts?.provider === 'flutterwave';
    const callback = opts?.callbackUrl ?? (needsCallback ? getWalletFundCallbackUrl() : undefined);
    if (callback) {
        body.callback_url = callback;
        body.callbackUrl = callback;
    }
    if (opts?.provider === 'stripe') {
        body.payment_method_type = 'card';
        if (opts.saveCard != null) body.save_card = opts.saveCard;
    }
    if (preset && isAdminTestPreset(preset) && preset.fund_request) {
        body.amount = preset.fund_request.amount;
        body.wallet_credit_usd_cents = preset.wallet_credit.amount_cents;
        body.currency = preset.fund_request.currency.toLowerCase();
        body.payment_input_mode = 'wallet_credit_usd';
    } else if ((opts?.provider === 'paystack' || opts?.provider === 'flutterwave') && preset && opts.feePreview) {
        const gross = grossChargeCentsFromSalesCalc(opts.feePreview);
        if (gross != null && gross >= 100) {
            body.payment_input_mode = 'wallet_credit_payment';
            body.amount = gross;
            body.payment_amount_cents = gross;
            body.wallet_credit_usd_cents = resolveWalletCreditUsdCentsFromSalesCalc(
                opts.feePreview,
                preset.wallet_credit.amount_cents,
            );
            body.currency = (opts.fundOptions?.payment_currency ?? 'ngn').toLowerCase();
        }
    } else if (
        (opts?.provider === 'paystack' || opts?.provider === 'flutterwave') &&
        !preset &&
        opts.customPaymentChargeCents != null &&
        opts.customPaymentChargeCents >= 100 &&
        isCustomGrossPaymentMode(opts.fundOptions ?? null) &&
        opts.feePreview
    ) {
        const gross = grossChargeCentsFromSalesCalc(opts.feePreview) ?? opts.customPaymentChargeCents ?? null;
        const walletCreditFromPreview = resolveWalletCreditUsdCentsFromSalesCalc(opts.feePreview, walletCreditUsdCents);
        if (gross != null && gross >= 100 && walletCreditFromPreview > 0) {
            body.payment_input_mode = 'gross_payment';
            body.amount = gross;
            body.payment_amount_cents = gross;
            body.wallet_credit_usd_cents = walletCreditFromPreview;
            body.currency = (opts.fundOptions?.payment_currency ?? 'ngn').toLowerCase();
        }
    } else {
        body.wallet_credit_usd_cents = walletCredit;
        body.amount = walletCredit;
        if (preset?.fund_request?.currency && opts?.provider === 'stripe') {
            body.currency = preset.fund_request.currency;
        } else if (opts?.fundOptions?.payment_currency && opts?.provider === 'stripe') {
            body.currency = opts.fundOptions.payment_currency.toLowerCase();
        }
    }
    return body;
}

/** Full POST /campaign-wallet/fund body for Stripe card / wallet pay (G Pay, Apple Pay). */
export function buildWalletFundStripeDispatchPayload(params: {
    walletCreditUsdCents: number;
    saveCard: boolean;
    preset?: IWalletFundPreset | null;
    activePreset?: IWalletFundPreset | null;
    fundOptions?: IWalletFundOptions | null;
    paymentCurrency: string;
    userEmail?: string;
    userName?: string;
}): IFundWalletPayload {
    const metadataObj = {
        category: 'campaign_wallet_funding',
        currency: params.paymentCurrency,
        metadata: {
            purpose: 'campaign_wallet_funding',
            email: params.userEmail,
            name: params.userName,
        },
    };
    const fundBody = buildCampaignWalletFundBody(params.walletCreditUsdCents, {
        provider: 'stripe',
        saveCard: params.saveCard,
        preset: params.preset ?? params.activePreset,
        fundOptions: params.fundOptions,
    });
    return {
        ...fundBody,
        payment_method_type: 'card',
        save_card: params.saveCard,
        metadata: JSON.stringify({
            ...metadataObj,
            currency: fundBody.currency ?? params.paymentCurrency,
        }),
    };
}

export function storePaystackFundReference(reference: string, provider: 'paystack' | 'flutterwave' = 'paystack'): void {
    try {
        sessionStorage.setItem(WALLET_FUND_PAYSTACK_REF_KEY, reference);
        sessionStorage.setItem(WALLET_FUND_CHECKOUT_PROVIDER_KEY, provider);
    } catch {
        /* ignore */
    }
}

export function readPaystackFundReference(): string | null {
    try {
        return sessionStorage.getItem(WALLET_FUND_PAYSTACK_REF_KEY);
    } catch {
        return null;
    }
}

export function readWalletFundCheckoutProvider(): 'paystack' | 'flutterwave' | null {
    try {
        const raw = sessionStorage.getItem(WALLET_FUND_CHECKOUT_PROVIDER_KEY);
        if (raw === 'paystack' || raw === 'flutterwave') return raw;
        return null;
    } catch {
        return null;
    }
}

export function clearPaystackFundReference(): void {
    try {
        sessionStorage.removeItem(WALLET_FUND_PAYSTACK_REF_KEY);
        sessionStorage.removeItem(WALLET_FUND_CHECKOUT_PROVIDER_KEY);
    } catch {
        /* ignore */
    }
}
